import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'cart-progress-discount';
const SYNC_DELAY_MS = 200;

/**
 * @returns {{ enabled: boolean; code: string; minItems: number }}
 */
function getProgressDiscountSettings() {
  const config = window.Theme?.cartProgressDiscount ?? {};
  const fromDom = document.querySelector('.cart-progress-discount-data');

  const code = (config.code?.trim() || fromDom?.dataset?.discountCode?.trim() || '').trim();
  const minItems = Number(config.minItems || fromDom?.dataset?.minItems) || 2;
  const enabled = config.enabled === true;

  return { enabled, code, minItems };
}

/**
 * @returns {string[]}
 */
function getCartSectionIds() {
  /** @type {Set<string>} */
  const ids = new Set();

  document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
    if (node instanceof HTMLElement && node.dataset.sectionId) {
      ids.add(node.dataset.sectionId);
    }
  });

  const fromDom = document.querySelector('.cart-progress-discount-data')?.dataset?.sectionId;
  if (fromDom) ids.add(fromDom);

  return [...ids];
}

/**
 * @param {{ items: Array<{ quantity: number }> }} cart
 * @param {number} minItems
 */
function isCartEligible(cart, minItems) {
  const itemCount = cart.items?.reduce((total, item) => total + item.quantity, 0) ?? 0;
  return itemCount >= minItems;
}

/**
 * @param {{ discount_codes?: Array<{ code: string; applicable: boolean }>; total_discount?: number; items?: Array<{ line_level_discount_allocations?: unknown[] }> }} cart
 * @param {string} code
 */
function isProgressDiscountApplied(cart, code) {
  const normalizedCode = code.toUpperCase();
  const codeEntry = cart.discount_codes?.find(
    (/** @type {{ code: string; applicable: boolean }} */ entry) => entry.code.toUpperCase() === normalizedCode
  );

  if (codeEntry?.applicable !== true) return false;

  if ((cart.total_discount ?? 0) > 0) return true;

  return (
    cart.items?.some((item) => (item.line_level_discount_allocations?.length ?? 0) > 0) ?? false
  );
}

/**
 * @param {string} discount
 * @param {string[]} sectionIds
 */
async function updateCartDiscount(discount, sectionIds) {
  const body = JSON.stringify({
    discount,
    ...(sectionIds.length ? { sections: sectionIds } : {}),
  });

  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));

  if (!response.ok) {
    throw new Error(`Cart discount update failed (${response.status})`);
  }

  return response.json();
}

/**
 * @param {object} data
 * @param {string[]} sectionIds
 */
function morphCartSections(data, sectionIds) {
  let morphed = false;

  for (const sectionId of sectionIds) {
    const sectionHtml = data.sections?.[sectionId];
    if (sectionHtml) {
      morphSection(sectionId, sectionHtml);
      morphed = true;
    }
  }

  if (morphed) {
    document.dispatchEvent(
      new CartUpdateEvent({}, sectionIds[0] ?? '', {
        source: SOURCE,
        sections: data.sections,
      })
    );
  } else if (sectionIds.length > 0) {
    document.dispatchEvent(
      new CartUpdateEvent({}, sectionIds[0] ?? '', {
        source: SOURCE,
      })
    );
  }

  return morphed;
}

let isSyncing = false;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let syncTimer;

/**
 * @param {string} source
 */
export async function syncProgressDiscount(source = SOURCE) {
  if (isSyncing) return;

  const settings = getProgressDiscountSettings();
  if (!settings.code) return;

  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();
  const sectionIds = getCartSectionIds();
  const normalizedCode = settings.code.toUpperCase();

  const existingCodes =
    cart.discount_codes?.map((/** @type {{ code: string }} */ entry) => entry.code).filter(Boolean) ?? [];

  const hasCode = existingCodes.some((code) => code.toUpperCase() === normalizedCode);

  if (!settings.enabled) {
    if (!hasCode) return;

    isSyncing = true;

    try {
      const nextCodes = existingCodes.filter((code) => code.toUpperCase() !== normalizedCode);
      const data = await updateCartDiscount(nextCodes.join(','), sectionIds);
      morphCartSections(data, sectionIds);
    } catch (error) {
      console.error('[cart-progress-discount]', error);
    } finally {
      isSyncing = false;
    }

    return;
  }

  const eligible = isCartEligible(cart, settings.minItems);
  const discountApplied = isProgressDiscountApplied(cart, settings.code);

  if (eligible && discountApplied) return;
  if (!eligible && !hasCode) return;

  const nextCodes = eligible
    ? [...existingCodes.filter((code) => code.toUpperCase() !== normalizedCode), settings.code]
    : existingCodes.filter((code) => code.toUpperCase() !== normalizedCode);

  isSyncing = true;

  try {
    const data = await updateCartDiscount(nextCodes.join(','), sectionIds);
    const updatedCart = data.items ? data : await fetch('/cart.js').then((response) => response.json());

    morphCartSections(data, sectionIds);

    if (eligible && !isProgressDiscountApplied(updatedCart, settings.code) && source !== 'retry') {
      window.setTimeout(() => syncProgressDiscount('retry'), 500);
    }
  } catch (error) {
    console.error('[cart-progress-discount]', error);
  } finally {
    isSyncing = false;
  }
}

/**
 * @param {string} [source]
 */
function scheduleSync(source = SOURCE) {
  if (syncTimer) window.clearTimeout(syncTimer);
  syncTimer = window.setTimeout(() => {
    syncProgressDiscount(source);
  }, SYNC_DELAY_MS);
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event instanceof CartUpdateEvent && event.detail?.data?.source === SOURCE) return;
  scheduleSync();
});

document.addEventListener('cart-drawer:open', () => {
  scheduleSync('drawer-open');
});

document.addEventListener(
  'click',
  async (event) => {
    if (!(event.target instanceof Element)) return;

    const checkoutTrigger = event.target.closest('#checkout, [name="checkout"]');
    if (!checkoutTrigger || checkoutTrigger.dataset.progressDiscountSyncing === 'true') return;

    const settings = getProgressDiscountSettings();
    if (!settings.enabled || !settings.code) return;

    const cart = await fetch('/cart.js').then((response) => response.json());
    const eligible = isCartEligible(cart, settings.minItems);
    if (!eligible) return;

    if (isProgressDiscountApplied(cart, settings.code)) return;

    event.preventDefault();

    checkoutTrigger.dataset.progressDiscountSyncing = 'true';

    try {
      await syncProgressDiscount('checkout');
    } finally {
      delete checkoutTrigger.dataset.progressDiscountSyncing;

      const formId = checkoutTrigger.getAttribute('form');
      const form =
        (formId ? document.getElementById(formId) : null) ??
        (checkoutTrigger instanceof HTMLButtonElement ? checkoutTrigger.form : null) ??
        checkoutTrigger.closest('form');

      if (form instanceof HTMLFormElement) {
        form.submit();
      }
    }
  },
  { capture: true }
);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scheduleSync('load'));
} else {
  scheduleSync('load');
}
